import React, { useEffect, useState } from "react";
import { inviteMember } from "../../services/adminService";

export interface InviteModalProps {
  getToken: () => string | undefined;
  onClose: () => void;
  onSuccess: (email: string) => void;
  onError: (message: string) => void;
  /**
   * When true, the Role field is not rendered at all — used by TeamPage,
   * where a manager may only ever create plain members and choosing a role
   * would offer a control that does nothing (the server ignores it on
   * POST /team/members). Defaults to false for the admin console.
   */
  hideRoleSelector?: boolean;
  /**
   * Overrides the default admin invite call (adminService.inviteMember).
   * TeamPage passes a callback that hits POST /team/members instead, which
   * takes only an email — `role` is still passed through for signature
   * symmetry but ignored by that callback.
   */
  onInvite?: (
    email: string,
    role: AssignableRole,
    token?: string,
  ) => Promise<void>;
}

/**
 * Roles this dropdown may offer. Must mirror the backend's assignable-role
 * whitelist (AdminService.validateRole) — an option added here without a
 * matching backend entry would just fail server-side on submit.
 */
type AssignableRole = "stytch_member" | "maru_manager" | "stytch_admin";

/**
 * InviteModal — invite a new member by email with a role assigned at invite
 * time. Default role is stytch_member (least-privilege). Submitting disables
 * both buttons and swaps "Send Invite" for an hourglass spinner.
 */
const InviteModal: React.FC<InviteModalProps> = ({
  getToken,
  onClose,
  onSuccess,
  onError,
  hideRoleSelector = false,
  onInvite,
}) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AssignableRole>("stytch_member");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, submitting]);

  const handleSubmit = async () => {
    // Disallow whitespace and embedded '@' in either local or domain part.
    // Trim first and pass the trimmed value through to inviteMember so the
    // success toast does not render an un-normalized email.
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setValidationError("Enter a valid email address.");
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    try {
      if (onInvite) {
        await onInvite(trimmed, role, getToken());
      } else {
        await inviteMember(trimmed, role, getToken());
      }
      onSuccess(trimmed);
      onClose();
    } catch (e: unknown) {
      // Surface the underlying error message (often "Email already exists" or
      // "Member already invited") instead of swallowing it behind a generic
      // string. adminService throws Error subclasses with the server's
      // response text in .message when available.
      const msg = e instanceof Error && e.message
        ? e.message
        : "Failed to send invite. Try again.";
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/60"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />
      <div className="relative bg-surface-container rounded-lg border border-outline-variant/30 shadow-2xl w-full max-w-md mx-4 p-6 animate-[slideDown_150ms_ease-out]">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-headline font-bold uppercase tracking-wide text-on-surface">
            Invite New Member
          </h3>
          <button
            onClick={() => {
              if (!submitting) onClose();
            }}
            disabled={submitting}
            className="text-slate-500 hover:text-on-surface transition-colors disabled:opacity-50"
            aria-label="Close invite modal"
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>

        {/* Wrap fields in <form> so Enter from the email input submits. Cancel
            uses type="button" so it does not also trigger submission. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitting) handleSubmit();
          }}
          className="space-y-5"
          noValidate
        >
          <div>
            <label
              htmlFor="invite-email"
              className="block text-xs font-label font-bold uppercase tracking-wider text-slate-400 mb-2"
            >
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              disabled={submitting}
              className="w-full px-3 py-2 bg-surface-container-low border border-outline-variant/30 rounded-md text-sm font-body text-on-surface placeholder-slate-500 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 disabled:opacity-50"
            />
            {validationError && (
              <p className="text-xs text-error mt-1">{validationError}</p>
            )}
          </div>

          {!hideRoleSelector && (
            <div>
              <label
                htmlFor="invite-role"
                className="block text-xs font-label font-bold uppercase tracking-wider text-slate-400 mb-2"
              >
                Role
              </label>
              <select
                id="invite-role"
                value={role}
                onChange={(e) => setRole(e.target.value as AssignableRole)}
                disabled={submitting}
                className="w-full px-3 py-2 bg-surface-container-low border border-outline-variant/30 rounded-md text-sm font-body text-on-surface focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 cursor-pointer disabled:opacity-50"
              >
                <option value="stytch_member">Member</option>
                <option value="maru_manager">Manager</option>
                <option value="stytch_admin">Admin</option>
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-xs font-label font-bold uppercase tracking-tighter text-slate-400 hover:text-on-surface transition-colors rounded-md hover:bg-surface-container-high disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-primary text-on-primary font-bold text-xs rounded-md uppercase tracking-tighter font-label transition-all active:opacity-80 disabled:opacity-50"
            >
              {submitting ? (
                <span className="material-symbols-outlined text-base">hourglass_empty</span>
              ) : (
                "Send Invite"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteModal;
